/**
 * What the create form can decide before it sends anything, and what it deliberately cannot.
 *
 * Pure, for the reason this directory keeps re-finding: a form's rules are decisions about
 * values, and a decision about a value should not need a browser to check. The form itself is
 * then wiring.
 *
 * ## The check-digit teaching arrived at c92, and the two paragraphs it replaces were both
 * true when they were written
 *
 * This file used to say, at length, that it validated **shape only** and said nothing about
 * the check digit — because at c34 the API said nothing about it either, and *"a client
 * stricter than its server teaches a user the value is checked when the API would accept
 * it."* That reasoning was correct and it is no longer load-bearing: **R12 put the check
 * digit inside `createProductSchema` itself**, so the arithmetic is the server's rule, this
 * form runs the server's rule, and the two cannot disagree by construction.
 *
 * What lands here now is `screens.md` §1's teaching — *"rule → likely cause → one-click fix
 * carrying the corrected number"* — and this is its ruled arrival point. The corrected value
 * is on the zod issue as `params.corrected`, which is a thing only a client validating with
 * the schema **directly** can see: `refuseFromSchema` forwards the code and the message and
 * nothing else, so a machine client over REST gets the number as prose inside `detail` and
 * this form gets it as a value. That asymmetry is R12's, argued in
 * `packages/shared/src/api/products.ts`, and it is the whole reason a button here can carry
 * a number.
 *
 * ## The correction is offered and never applied
 *
 * §9 rules that the platform never rewrites a customer's GTIN — check-digit failures in the
 * import pipeline are skip-only — and this is the same ruling at a different door. So
 * {@link checkDigitTeaching} produces a *proposal* and {@link applyGtinCorrection} is a
 * separate function the **user's click** calls. A form that arrived already corrected would
 * have made a decision about a number that identifies somebody else's product, silently, in
 * the one field where being wrong means a barcode no scanner will ever read.
 *
 * The copy deliberately does not claim to know which digit was mistyped — any single-digit
 * error or transposition changes the sum, so the arithmetic proves the code wrong and says
 * nothing about where. What it *can* mark is the check digit itself, and that is what
 * {@link CHECK_DIGIT_MARK} does: the fourteenth character is the one that does not agree
 * with the other thirteen, which is exactly what the correction changes and exactly what is
 * being claimed. Marking any other digit would be a guess wearing a highlight.
 */
import type { InvalidState } from "@packagepage/ui";
import { createProductSchema, type CreateProductInput } from "@packagepage/shared";

/** What the form holds. Strings throughout, because that is what an input gives you. */
export interface CreateProductForm {
  readonly gtin14: string;
  readonly brandName: string;
  readonly sku: string;
  readonly nameEn: string;
  readonly workspaceId: string;
}

export const EMPTY_FORM: CreateProductForm = {
  gtin14: "",
  brandName: "",
  sku: "",
  nameEn: "",
  workspaceId: "",
};

/**
 * The form's fields as the shared schema's input, with empty optionals **omitted**.
 *
 * `sku` and `name` are optional in the schema, and an empty text input yields `""` — which
 * is a *value*, and a different request from "no SKU". `sku: ""` would fail the schema's
 * `.min(1)`, and `name: { en: "" }` would store an empty English name that
 * `displayName` then has to fall back out of. Omitting is what the user meant by leaving it
 * blank.
 */
export function toCreateInput(form: CreateProductForm): unknown {
  const sku = form.sku.trim();
  const nameEn = form.nameEn.trim();
  return {
    gtin14: form.gtin14.trim(),
    brandName: form.brandName.trim(),
    // Trimmed like every other field. It was not, until a test asserted the header's own
    // sentence — "whitespace is trimmed" — against the code and found it false for this one.
    // Today nothing binds `workspaceId` to an input, so the exposure is nil; that is an
    // argument about the current UI and not about this function, which is a general
    // contract over `CreateProductForm` and is tested as one.
    workspaceId: form.workspaceId.trim(),
    ...(sku === "" ? {} : { sku }),
    ...(nameEn === "" ? {} : { name: { en: nameEn } }),
  };
}

/** A refusal, keyed by the field it belongs beside. */
export type FieldErrors = Readonly<Partial<Record<keyof CreateProductForm, string>>>;

/**
 * Validate with the server's own schema and place each message beside its field.
 *
 * **The messages are not written here.** They are `createProductSchema`'s, which are the
 * strings the API answers with — c29's rule that refusal copy is authored once, and R7's
 * that nothing is authored twice. A form that phrased its own version would teach the user
 * one sentence and the API another for the same mistake.
 *
 * An issue whose path does not name a field this form holds is returned under `gtin14`
 * rather than dropped. Dropping it would leave a form that refuses to submit and says
 * nothing — the failure mode worth more than tidiness here.
 *
 * **Two branches below are currently unreachable, and they are kept deliberately** — found
 * by the test agent, which verified it empirically rather than assuming and declined to
 * write assertions that could not fail. Recorded here so the next reader does not mistake
 * either for live code, or delete them as dead:
 *
 *   - **`name` → `nameEn`.** Every `CreateProductForm` field is a string, so
 *     {@link toCreateInput} can only ever emit `{ en: <string> }`, which always satisfies
 *     `z.record(z.string(), z.string())`. The remap goes live the day this form grows a
 *     second language field — which is when per-field language switching reaches it.
 *   - **The non-string `path[0]` fallback.** `createProductSchema` contains no arrays, so
 *     no issue can have one today. It goes live the day the schema does, and its whole
 *     purpose is that such a day arrives without anybody remembering this function.
 *
 * Both are the *"remove the need for the branch"* question asked and answered the other
 * way: removing them buys nothing now and costs a silent dropped error later.
 */
export function validateCreateForm(form: CreateProductForm): FieldErrors {
  const parsed = createProductSchema.safeParse(toCreateInput(form));
  if (parsed.success) return {};

  const errors: Record<string, string> = {};
  for (const issue of parsed.error.issues) {
    const field = issue.path[0];
    const key =
      field === "name" ? "nameEn" : typeof field === "string" ? field : "gtin14";
    // First message per field wins: zod reports every failing rule, and a field showing
    // three sentences at once is a field nobody reads.
    errors[key] ??= issue.message;
  }
  return errors;
}

/**
 * The corrected GTIN this form was offered, or `null`.
 *
 * Read off the zod issue's `params.corrected`, which `createProductSchema` attaches to the
 * check-digit refusal and to nothing else — so this returns a value **only** for the one
 * failure that has a computable answer. `INVALID_GTIN` has none by construction (R12's whole
 * argument for two codes) and answers `null` here, which is what stops a fix button
 * appearing beside a value nobody can correct.
 *
 * `params` is typed `Record<string, unknown>` by zod, so the narrowing is real rather than a
 * cast: a `corrected` that is not a string is treated as absent.
 */
export function correctedGtinFor(form: CreateProductForm): string | null {
  const parsed = createProductSchema.safeParse(toCreateInput(form));
  if (parsed.success) return null;
  for (const issue of parsed.error.issues) {
    if (issue.path[0] !== "gtin14") continue;
    const params: unknown = (issue as { params?: unknown }).params;
    if (typeof params !== "object" || params === null) continue;
    const corrected: unknown = (params as { corrected?: unknown }).corrected;
    if (typeof corrected === "string") return corrected;
  }
  return null;
}

/**
 * Where the offending character is, for `Input`'s in-place mark.
 *
 * A GTIN-14's check digit is the fourteenth character — index 13, half-open to 14. See the
 * file header on why this and no other digit is marked: it is the one that does not agree
 * with the other thirteen, which is the whole content of the arithmetic, and any other
 * highlight would be a guess about which digit was mistyped.
 */
export const CHECK_DIGIT_MARK = { from: 13, to: 14 } as const;

/**
 * `screens.md` §1's teaching, as a value: **rule → likely cause → one-click fix**, in that
 * order, carrying the corrected number.
 *
 * `components.md` makes the order a contract rather than a preference and `Input`'s
 * `InvalidState` makes it a compile fact — a failure with no `cause` does not typecheck. The
 * fix's **label is the corrected value**, so the button doubles as the explanation: *"Change
 * to 09506000134376"* has already said what it will do, and somebody who does not want it
 * now knows exactly what they were being offered.
 *
 * Returns `null` when the GTIN is fine, when it is empty, and — importantly — when the
 * failure is `INVALID_GTIN` rather than the check digit, because that one has no correction
 * to carry and {@link validateCreateForm}'s plain message is the right treatment.
 *
 * **Nothing here applies anything.** The returned `fix.value` is a proposal; the *user's*
 * click calls {@link applyGtinCorrection}. That separation is §9's ruling expressed as two
 * functions rather than as a comment somebody has to keep.
 */
export function checkDigitTeaching(form: CreateProductForm): InvalidState | null {
  const corrected = correctedGtinFor(form);
  if (corrected === null) return null;
  return {
    state: "invalid",
    rule:
      "The last digit of a GTIN is a check digit computed from the other thirteen, and " +
      "this one does not match them.",
    cause:
      "Usually a transposed pair — 34 typed as 43 — or a digit dropped while retyping. " +
      "Both are invisible to read back, which is what the check digit is for.",
    fix: { label: `Change to ${corrected}`, value: corrected },
    markFrom: CHECK_DIGIT_MARK.from,
    markTo: CHECK_DIGIT_MARK.to,
  };
}

/**
 * Accept the proposal — **called by a click, never by a render.**
 *
 * Returns a new form rather than mutating one, so the two directions the brief asks to be
 * asserted are two values a test can compare: the form before is unchanged, the form after
 * carries the corrected number. Nothing else moves.
 */
export function applyGtinCorrection(
  form: CreateProductForm,
  corrected: string,
): CreateProductForm {
  return { ...form, gtin14: corrected };
}

/**
 * Whether the form is submittable — **derived, never a separate flag.**
 *
 * A `canSubmit` boolean maintained alongside the errors is two representations of one fact,
 * and they disagree on the render where one is updated and the other is not.
 */
export function canSubmit(form: CreateProductForm): boolean {
  return Object.keys(validateCreateForm(form)).length === 0;
}

/** The typed input, once it is known to be valid. Throws if it is not — call {@link canSubmit}. */
export function parseCreateForm(form: CreateProductForm): CreateProductInput {
  return createProductSchema.parse(toCreateInput(form));
}
